// src/serviceWorkerRegistration.js
// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl, { updateViaCache: 'none' })
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://cra.link/PWA.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      setupAutomaticUpdates(registration);
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

export function setupAutomaticUpdates(registration, dependencies = {}) {
  const serviceWorker = dependencies.serviceWorker || navigator.serviceWorker;
  const documentObject = dependencies.documentObject || document;
  const reloadPage = dependencies.reloadPage || (() => window.location.reload());
  const workersPendingActivation = new WeakSet();
  const activatedWorkers = new WeakSet();
  const previousUpdateFound = registration.onupdatefound;
  const hadControllerWhenRegistered = Boolean(serviceWorker.controller);
  let updateCheckDepth = 0;
  let shouldReloadAfterUpdate = false;
  let isReloading = false;
  let wasHidden = documentObject.visibilityState === 'hidden';

  const requestWorkerActivation = (worker) => {
    const workerToActivate = registration.waiting || worker;

    if (!serviceWorker.controller || !workerToActivate || activatedWorkers.has(workerToActivate)) {
      return;
    }

    activatedWorkers.add(workerToActivate);
    shouldReloadAfterUpdate = true;

    try {
      workerToActivate.postMessage({ type: 'SKIP_WAITING' });
    } catch (error) {
      activatedWorkers.delete(workerToActivate);
      shouldReloadAfterUpdate = false;
      console.warn('Não foi possível ativar a nova versão do app.', error);
    }
  };

  const activateWorkerWhenInstalled = (worker) => {
    if (!worker || workersPendingActivation.has(worker)) {
      return;
    }

    if (worker.state === 'installed') {
      requestWorkerActivation(worker);
      return;
    }

    workersPendingActivation.add(worker);

    const handleStateChange = () => {
      if (worker.state !== 'installed') {
        return;
      }

      worker.removeEventListener?.('statechange', handleStateChange);
      requestWorkerActivation(worker);
    };

    worker.addEventListener?.('statechange', handleStateChange);
  };

  const applyAvailableUpdate = () => {
    if (registration.waiting) {
      requestWorkerActivation(registration.waiting);
    } else if (registration.installing) {
      activateWorkerWhenInstalled(registration.installing);
    }
  };

  const checkForUpdate = async () => {
    updateCheckDepth += 1;
    applyAvailableUpdate();

    try {
      await registration.update();
      applyAvailableUpdate();
    } catch (error) {
      // Falhas de conexão não impedem o app de continuar usando o conteúdo em cache.
      console.warn('Não foi possível verificar se existe uma nova versão do app.', error);
    } finally {
      updateCheckDepth -= 1;
    }
  };

  const handleUpdateFound = (event) => {
    if (typeof previousUpdateFound === 'function') {
      previousUpdateFound.call(registration, event);
    }

    if (updateCheckDepth > 0) {
      activateWorkerWhenInstalled(registration.installing);
    }
  };

  const handleControllerChange = () => {
    // O novo worker usa skipWaiting. Se a página já era controlada, uma troca de
    // controller também pode ocorrer antes da mensagem de ativação ser enviada.
    if ((!shouldReloadAfterUpdate && !hadControllerWhenRegistered) || isReloading) {
      return;
    }

    isReloading = true;
    reloadPage();
  };

  const handleVisibilityChange = () => {
    if (documentObject.visibilityState === 'hidden') {
      wasHidden = true;
      return;
    }

    if (documentObject.visibilityState === 'visible' && wasHidden) {
      wasHidden = false;
      void checkForUpdate();
    }
  };

  registration.onupdatefound = handleUpdateFound;
  serviceWorker.addEventListener('controllerchange', handleControllerChange);
  documentObject.addEventListener('visibilitychange', handleVisibilityChange);

  const initialCheck = checkForUpdate();

  return {
    checkForUpdate,
    initialCheck,
    dispose: () => {
      serviceWorker.removeEventListener?.('controllerchange', handleControllerChange);
      documentObject.removeEventListener?.('visibilitychange', handleVisibilityChange);

      if (registration.onupdatefound === handleUpdateFound) {
        registration.onupdatefound = previousUpdateFound;
      }
    },
  };
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    cache: 'no-store',
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
